import React from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useSelector } from 'react-redux';
import strings from 'localization/strings';
import { makeStyles } from 'styles/util';
import { getStorageGroupTitle, onlyPeriodBookingsAllowed } from 'helpers/StorageSiteHelper';
import { createBookingPriceWithDiscountInfo } from 'helpers/PriceHelper';
import { formatLengthAndWidth } from 'helpers/StringHelper';
import rentalTypes from 'enums/rentalTypes';
import clsx from 'clsx';

import Radio from '@material-ui/core/Radio';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Amount from 'common/Amount';

const useStyles = makeStyles(({ theme, colors }) => ({
    storageGroupSelectorItem: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: colors.white,
        padding: theme.spacing(0.375, 1.5, 0.375, 0),
        borderBottom: `1px solid ${colors.secondaryColor}`,
        color: 'black',
        cursor: 'pointer',
        '&:last-child': {
            borderBottom: 'none'
        },
        '&:hover': {
            backgroundColor: colors.whiteHover
        }
    },
    radioButtonLabel: {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5)
    },
    radioButtonLabelLine1: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1)
    },
    storageGroupTitle: {
        flex: '1 1 auto'
    },
    priceContainer: {
        flex: '0 0 auto',
        textAlign: 'right'
    },
    price: {
        color: colors.primaryColor
    },
    notBookable: {
        color: `${colors.textColorDisabled} !important`,
        '& h6': {
            color: `${colors.textColorDisabled} !important`
        }
    },
    lengthAndWidth: {
        fontSize: '75%',
        color: colors.textColorLight
    },
    nonDiscountedPrice: {
        fontSize: '75%',
        color: colors.textColorLight,
        textDecoration: 'line-through',
        textDecorationThickness: '1.5px'
    },
    notBookablePrice: {
        fontSize: '75%'
    },
    discountDescription: {
        fontSize: '75%',
        color: colors.textColorLight
    },
    discountedPrice: {
        color: colors.red
    }
}));

const StorageGroupSelectorItem = ({ storageSite, storageGroup, storageGroupId, isBookable, disabled, showLengthAndWidth, requestedStartDate, requestedEndDate, onChange, closeModal }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const authenticationContext = useSelector(state => state.authentication.context);
    const { selectedActor } = authenticationContext;
    const maxLengthForDescriptionToBeOnLine1 = 20;

    const subscriptionBooking = !requestedEndDate && !onlyPeriodBookingsAllowed(storageSite);

    const booking = {
        startDate: requestedStartDate,
        endDate: subscriptionBooking ? undefined : requestedEndDate,
        subscriptionBooking,
        area: 1,
        tenantOrganizationType: selectedActor?.organizationType
    };

    const fieldName = `categories.${storageGroup.category}.storageGroupId`;
    const onClick = () => {
        if (isBookable && !disabled) {
            onChange({ name: fieldName, value: storageGroupId });
            closeModal();
        }
    };

    const priceSuffix = storageGroup.rentalType === rentalTypes.customArea.key
        ? `${strings.squareMeter}/${strings.monthAbbreviated}`
        : strings.monthAbbreviated;
    const priceFormatString = `{0}/${priceSuffix}`;

    const priceInfo = createBookingPriceWithDiscountInfo(booking, storageSite, storageGroup, true, priceFormatString, appContext);

    const amountElement = <Amount value={priceInfo.displayPrice} currency={storageGroup.currency} formatString={priceFormatString}/>;

    const getDiscountDescriptionLine1 = () => {
        if(priceInfo.untilFurtherDiscount) {
            return <Box className={classes.nonDiscountedPrice}><Amount value={priceInfo.nonDiscountedPrice} currency={storageGroup.currency} formatString={priceFormatString}/></Box>;
        }
        if(priceInfo.discountDescription?.length <= maxLengthForDescriptionToBeOnLine1) {
            return <Box className={clsx(classes.discountDescription, { [classes.discountedPrice]: !priceInfo.displayPriceIsDiscounted })}>{priceInfo.discountDescription}</Box>;
        }
        return undefined;
    };

    const getDiscountDescriptionLine2 = () => {
        if(!priceInfo.untilFurtherDiscount && priceInfo.discountDescription?.length > maxLengthForDescriptionToBeOnLine1) {
            return <Box className={clsx(classes.discountDescription, { [classes.discountedPrice]: !priceInfo.displayPriceIsDiscounted })}>{priceInfo.discountDescription}</Box>;
        }
        return undefined;
    };

    return (
        <Box className={clsx(classes.storageGroupSelectorItem, { [classes.notBookable]: !isBookable })} onClick={onClick}>
            <Radio name={fieldName} value={storageGroupId} disabled={disabled || !isBookable}/>
            <Box className={classes.radioButtonLabel}>
                <Box className={classes.radioButtonLabelLine1}>
                    <Box className={classes.storageGroupTitle}>
                        <Typography variant="subtitle1">{getStorageGroupTitle(storageGroup, appContext, { useSpecialCustomAreaLabel: true })}</Typography>
                        {
                            showLengthAndWidth && storageGroup.storageLength && storageGroup.storageWidth &&
                            (
                                <Box className={classes.lengthAndWidth}>{formatLengthAndWidth(storageGroup.storageLength, storageGroup.storageWidth, appContext, { numberOfDecimals: 1 })}</Box>
                            )
                        }
                    </Box>
                    <Box className={classes.priceContainer}>
                        {
                            isBookable &&
                            (
                                <>
                                    <Typography variant="subtitle1" display="block" className={clsx(classes.price, { [classes.discountedPrice]: priceInfo.displayPriceIsDiscounted })}>
                                        {amountElement}
                                    </Typography>
                                    {getDiscountDescriptionLine1()}
                                </>
                            )
                        }
                        {
                            !isBookable &&
                            (
                                <>
                                    <Typography variant="subtitle1" display="block">{strings.notBookableShort}</Typography>
                                    <Box className={classes.notBookablePrice}>{amountElement}</Box>
                                </>
                            )
                        }
                    </Box>
                </Box>
                {
                    isBookable && getDiscountDescriptionLine2() &&
                    (
                        <Box>
                            {getDiscountDescriptionLine2()}
                        </Box>
                    )
                }
            </Box>
        </Box>
    );
};

StorageGroupSelectorItem.propTypes = {
    storageSite: PropTypes.object.isRequired,
    storageGroup: PropTypes.object.isRequired,
    storageGroupId: PropTypes.number.isRequired,
    isBookable: PropTypes.bool,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    showLengthAndWidth: PropTypes.bool,
    requestedStartDate: PropTypes.object,
    requestedEndDate: PropTypes.object,
    onChange: PropTypes.func,
    closeModal: PropTypes.func
};

export default StorageGroupSelectorItem;
